// Facade pubblico dell'SDK CloseYourIt, isomorfo browser/Node. Mantiene un
// singleton di [Client]; tutti i metodi sono no-op finché non si chiama [init]
// e nessuno solleva. Nell'IIFE (`globalName: CloseYourIt`) questi export
// diventano i metodi di `window.CloseYourIt`. Le integrazioni installate
// dipendono dalla piattaforma: window handlers/breadcrumb/http/jank e, se
// richiesti, web vitals e fallimenti di risorse/CSP nel browser; process
// handlers + lifecycle su Node.

import { Client } from './client'
import { installBreadcrumbs } from './integrations/browser/breadcrumbs'
import { installGlobalHandlers } from './integrations/browser/globalHandlers'
import { installHttpInstrumentation } from './integrations/browser/http'
import { installJankDetection } from './integrations/browser/jank'
import { installPageviews } from './integrations/browser/pageviews'
import { installReplay } from './integrations/browser/replay'
import { installResourceErrors } from './integrations/browser/resourceErrors'
import { installWebVitals } from './integrations/browser/webVitals'
import { installNodeLifecycle } from './integrations/node/lifecycle'
import { installProcessHandlers } from './integrations/node/processHandlers'
import { CloseYourItLogger } from './logger'
import { isSecretToken, resolveOptions, resolvedOptionsEqual } from './options'
import { isBrowser, isNode } from './runtime'
import { Scope } from './scope'
import type {
  CaptureHint,
  CloseYourItOptions,
  EventLevel,
  LogLevel,
  TraceCarrier,
  UserContext,
} from './types'
import { emptyStatsSnapshot } from './stats'
import type { StatsSnapshot } from './stats'

let currentClient: Client | null = null
let uninstallHandlers: (() => void) | null = null
let uninstallBreadcrumbs: (() => void) | null = null
let uninstallHttp: (() => void) | null = null
let uninstallJank: (() => void) | null = null
let uninstallPageviews: (() => void) | null = null
let uninstallReplay: (() => void) | null = null
let uninstallWebVitals: (() => void) | null = null
let uninstallResourceErrors: (() => void) | null = null
let uninstallLifecycle: (() => void) | null = null

// Chiusure dei client sostituiti da una re-init, ancora in volo (CYJS-8). `init`
// resta sincrona e non le attende, ma `flush()`/`close()` sì: così gli eventi già in
// coda del vecchio client non si perdono se il processo esce subito dopo il cambio.
const pendingTeardowns = new Set<Promise<void>>()

function trackTeardown(closing: Promise<void>): void {
  const settled = closing
    .catch(() => {})
    .finally(() => {
      pendingTeardowns.delete(settled)
    })
  pendingTeardowns.add(settled)
}

/// Inizializza l'SDK. **Idempotente per opzioni equivalenti**: con la stessa
/// configurazione risolta ritorna il client esistente senza reinstallare gli handler,
/// anche se disabilitato da misconfig (non lo chiude e ricrea a vuoto). Con **opzioni
/// diverse riconfigura a caldo** (CYJS-8): smonta gli handler del client precedente e
/// ne avvia flush+close in background — gli eventi già in coda partono verso la
/// configurazione con cui erano stati accodati (attendibile via `flush()`/`close()`) —
/// trasferisce lo scope corrente (trace_id, utente, tag, breadcrumb) al nuovo client e
/// mette in uso la nuova config, senza bisogno di un `close()` manuale. Con config
/// incompleta resta no-op (non solleva) e può essere superata da una `init` valida.
export function init(options: CloseYourItOptions): Client {
  // Idempotenza per opzioni equivalenti: stessa configurazione risolta → no-op,
  // qualunque sia lo stato (attivo o disabilitato). Non si smontano né reinstallano
  // gli handler (nessun listener duplicato) e un client disabilitato non viene chiuso
  // e ricreato a ogni init identica.
  if (
    currentClient &&
    resolvedOptionsEqual(currentClient.options, resolveOptions(options))
  ) {
    return currentClient
  }

  // Re-init (o primo init, o superamento di un client disabilitato da misconfig):
  // smonta i listener del client precedente e ne avvia flush+close SENZA bloccare
  // init (che resta sincrona). La chiusura è tracciata così un flush()/close()
  // successivo la attende: gli eventi in coda del vecchio client non si perdono
  // all'uscita del processo. Lo scope corrente è trasferito al nuovo client, così
  // cambiare la sola config non azzera lo stato applicativo (utente, tag, trace).
  const previous = currentClient
  teardown()
  const client = new Client(options, previous ? { scope: previous.scope } : {})
  currentClient = client
  if (previous) trackTeardown(previous.close())

  // Senza questo warning una misconfig (es. token/projectId mancante) rende l'SDK un no-op
  // totalmente silenzioso: nessun evento inviato e nessun segnale al dev, nemmeno con debug:true.
  // Non avvisa quando la disabilitazione è voluta (options.enabled: false).
  if (!client.enabled && client.options.enabled) {
    console.warn(
      `[closeyourit] SDK disabilitato: ${client.disabledReason} — nessun evento verrà inviato`,
    )
  }

  // Nel browser il bearer segreto (cyi_) non deve nemmeno comparire nel bundle:
  // l'SDK non lo invia mai, ma se è presente nel codice client va rimosso.
  if (isBrowser() && isSecretToken(client.options.token)) {
    console.warn(
      '[closeyourit] bearer segreto (cyi_…) presente nel codice browser: è SERVER-ONLY e non verrà mai inviato. Rimuovilo dal bundle e usa `publicKey` (DSN public key).',
    )
  }

  if (client.enabled && client.options.autoInstall) {
    if (isBrowser()) {
      // Per primo: chi flusha su `pagehide` (replay) deve trovare il segnale di
      // pagina in uscita già alzato, altrimenti quell'invio non usa il beacon.
      uninstallLifecycle = installLifecycle(client)
      uninstallHandlers = installGlobalHandlers(client)
      if (client.options.breadcrumbs) {
        uninstallBreadcrumbs = installBreadcrumbs(client)
      }
      // Il patch http serve a due consumatori: breadcrumb http e/o verdetti
      // performance. Si installa se almeno uno è attivo.
      const wantsHttp =
        (client.options.breadcrumbs && client.options.autoBreadcrumbs.http) ||
        (client.options.detectPerformanceIssues && client.options.captureExternalHttp)
      if (wantsHttp) {
        uninstallHttp = installHttpInstrumentation(client)
      }
      if (client.options.detectPerformanceIssues && client.options.detectJank) {
        uninstallJank = installJankDetection(client)
      }
      if (client.options.trackPageviews) {
        uninstallPageviews = installPageviews(client)
      }
      if (client.options.replay) {
        uninstallReplay = installReplay(client)
      }
      // Dopo il lifecycle, come il replay: le misure partono a pagina in uscita
      // e devono trovare il beacon già in uso (CYJS-29).
      if (client.options.trackWebVitals) {
        uninstallWebVitals = installWebVitals(client)
      }
      if (client.options.captureResourceErrors) {
        uninstallResourceErrors = installResourceErrors(client)
      }
    } else if (isNode()) {
      uninstallHandlers = installProcessHandlers(client)
      uninstallLifecycle = installNodeLifecycle(client)
    }
  }

  return client
}

/// Cattura un'eccezione o valore arbitrario.
export function captureException(error: unknown, hint?: CaptureHint): void {
  currentClient?.captureException(error, hint)
}

/// Cattura un messaggio diagnostico.
export function captureMessage(message: string, level: EventLevel = 'info'): void {
  currentClient?.captureMessage(message, level)
}

/// Bufferizza un log strutturato (inviato in batch).
export function log(
  level: LogLevel,
  message: string,
  attributes?: Record<string, unknown>,
  loggerName?: string,
): void {
  currentClient?.log(level, message, attributes, loggerName)
}

/// Logger applicativo con metodi per livello (`logger.warn('…')`), null-safe.
/// `logger.named('payments')` crea un logger figlio con nome.
export const logger = new CloseYourItLogger(() => currentClient)

/// Misura [body] e invia una metrica `slow_method` oltre soglia. Preserva la
/// sincronicità del body. Pre-init esegue comunque il body senza misurare.
/// Label vuota → TypeError (sempre, anche pre-init: errore del programmatore).
export function measure<T>(
  label: string,
  body: () => T,
  opts?: { args?: Record<string, unknown> },
): T {
  if (currentClient) return currentClient.measure(label, body, opts)
  if (typeof label !== 'string' || label.trim().length === 0) {
    throw new TypeError(
      'closeyourit: measure(label) richiede una label non vuota (è il fingerprint della metrica)',
    )
  }
  return body()
}

/// Cattura una pageview (web analytics). No-op fuori dal browser o pre-init.
/// [overrides.path] forza il path (default `location.pathname`). Con
/// `trackPageviews: true` l'auto-tracking la chiama al load e a ogni cambio di
/// pathname; usarla a mano per SPA con routing custom o eventi virtuali.
export function capturePageview(overrides?: { path?: string }): void {
  currentClient?.capturePageview(overrides)
}

/// Cattura un custom event (goal/conversione). [name] è il nome dell'evento
/// (es. "Signup"); no-op fuori dal browser o pre-init.
export function captureEvent(name: string, overrides?: { path?: string }): void {
  currentClient?.captureEvent(name, overrides)
}

/// Imposta l'utente corrente (`null` per rimuoverlo).
export function setUser(user: UserContext | null): void {
  currentClient?.setUser(user)
}

/// Imposta un tag.
export function setTag(key: string, value: string): void {
  currentClient?.setTag(key, value)
}

/// Imposta più tag in un colpo.
export function setTags(tags: Record<string, string>): void {
  currentClient?.setTags(tags)
}

/// Imposta un valore extra.
export function setExtra(key: string, value: unknown): void {
  currentClient?.setExtra(key, value)
}

/// Imposta un context nominato.
export function setContext(key: string, value: Record<string, unknown>): void {
  currentClient?.setContext(key, value)
}

/// Imposta l'ID di correlazione (default: generato per pagina).
export function setTraceId(id: string | undefined): void {
  currentClient?.setTraceId(id)
}

/// ID di correlazione corrente, o `undefined` se non inizializzato.
export function getTraceId(): string | undefined {
  return currentClient?.scope.traceId
}

/// Adotta un trace context W3C in ingresso da [carrier] (header di richiesta
/// server/SSR, un `Headers`, o un getter): un `traceparent` valido diventa il
/// `trace_id` di correlazione e aggancia errori/log al trace distribuito.
/// No-op pre-init o su header assente/malformato.
export function continueTrace(carrier: TraceCarrier): void {
  currentClient?.continueTrace(carrier)
}

/// Header W3C Trace Context (`traceparent`, più `tracestate`/`baggage` se
/// adottati) da iniettare in una richiesta in uscita verso [target]. `{}` se
/// pre-init/disabilitato o se [target] non è fidato (`tracePropagationTargets` o
/// same-origin): trace interno e baggage non raggiungono host non fidati. Senza
/// [target] la destinazione è first-party e la propagazione è consentita.
export function getTraceHeaders(target?: string): Record<string, string> {
  return currentClient?.getTraceHeaders(target) ?? {}
}

/// ID della sessione di replay in corso, o `undefined` se il replay non è attivo.
export function getReplaySessionId(): string | undefined {
  return currentClient?.scope.replaySessionId
}

/// Aggiunge un breadcrumb manuale.
export function addBreadcrumb(input: {
  message?: string
  category?: string
  type?: string
  level?: string
  data?: Record<string, unknown>
}): void {
  currentClient?.addBreadcrumb(input)
}

/// Azzera lo scope (user/tag/extra/context/breadcrumbs), tranne il trace_id.
export function clearScope(): void {
  currentClient?.clearScope()
}

/// Esegue [fn] in uno scope isolato per l'intero flusso asincrono: nuovo trace_id
/// (correlazione per-richiesta) e breadcrumb propri. Su Node tiene separati i dati
/// di richieste concorrenti (AsyncLocalStorage); nel browser (single-thread) isola
/// per la durata sincrona di [fn]. Va avvolto attorno all'handler di richiesta (es.
/// middleware server) così user/tag/request/breadcrumb non attraversano il confine
/// tra richieste (CYJS-7). Pre-init esegue [fn] senza isolamento. Ritorna il valore
/// di [fn] (una Promise è propagata: l'isolamento copre l'await).
export function runWithScope<T>(fn: (scope: Scope) => T): T {
  if (currentClient) return currentClient.runWithScope(fn)
  return fn(new Scope())
}

/// Esegue [fn] con uno scope temporaneo forkato da quello corrente (trace_id e
/// breadcrumb ereditati): le modifiche fatte dentro non escono. Utile per una
/// cattura con contesto aggiuntivo senza sporcare lo scope condiviso. Pre-init
/// esegue [fn] senza isolamento.
export function withScope<T>(fn: (scope: Scope) => T): T {
  if (currentClient) return currentClient.withScope(fn)
  return fn(new Scope())
}

/// Snapshot dei contatori diagnostici: aggregati (`enqueued`, `sent`, `dropped`,
/// `failed`, `timeout`) più `reasons`, la ripartizione di scarti e fallimenti per
/// motivo (`sample_rate`, `before_send`, `error`, `queue_full`, `response`,
/// `network`). Pura lettura in-memory: non invia mai telemetria (CYJS-9).
export function getStats(): StatsSnapshot {
  return currentClient?.stats.toObject() ?? emptyStatsSnapshot()
}

/// `true` se inizializzato e la configurazione consente l'invio.
export function isEnabled(): boolean {
  return currentClient?.enabled ?? false
}

/// Svuota i log bufferizzati e attende gli invii in volo, incluse le chiusure dei
/// client sostituiti da una re-init ancora in corso (CYJS-8).
export function flush(): Promise<void> {
  return Promise.all([
    currentClient?.flush() ?? Promise.resolve(),
    ...pendingTeardowns,
  ]).then(() => undefined)
}

/// Spegne l'SDK, ripristina i patch globali e libera le risorse. `currentClient` è
/// azzerato SUBITO (prima dell'await): una `init` concorrente durante la chiusura
/// installa il nuovo client senza che questo `close()` lo azzeri al termine (CYJS-8).
export async function close(): Promise<void> {
  const client = currentClient
  teardown()
  currentClient = null
  await Promise.all([client?.close() ?? Promise.resolve(), ...pendingTeardowns])
}

function teardown(): void {
  uninstallHandlers?.()
  uninstallBreadcrumbs?.()
  uninstallHttp?.()
  uninstallJank?.()
  uninstallPageviews?.()
  uninstallReplay?.()
  uninstallWebVitals?.()
  uninstallResourceErrors?.()
  uninstallLifecycle?.()
  uninstallHandlers = null
  uninstallBreadcrumbs = null
  uninstallHttp = null
  uninstallJank = null
  uninstallPageviews = null
  uninstallReplay = null
  uninstallWebVitals = null
  uninstallResourceErrors = null
  uninstallLifecycle = null
}

// Su `pagehide`/`visibilitychange(hidden)` segnala al client che la pagina sta
// uscendo di scena e svuota i log. Il segnale viene PRIMA del flush perché da lì
// in poi gli invii passano dal beacon: `fetch(keepalive)` non sopravvive alla
// chiusura della scheda su Safari iOS (CYJS-29). `pageshow` (ritorno da bfcache)
// e il ritorno in primo piano rimettono in uso il fetch, che a pagina viva dà la
// risposta su cui si regge la diagnostica.
//
// Va installato per PRIMO tra gli handler browser: gli ascoltatori di `pagehide`
// girano nell'ordine di registrazione, e quelli delle altre integrazioni (replay)
// devono trovare il segnale già alzato per partire anch'essi col beacon.
function installLifecycle(client: Client): () => void {
  if (typeof window === 'undefined') return () => {}

  const onHide = (): void => {
    client.notifyPageHiding(true)
    void client.flush()
  }
  const onShow = (): void => {
    client.notifyPageHiding(false)
  }
  const onVisibility = (): void => {
    if (typeof document === 'undefined') return
    if (document.visibilityState === 'hidden') onHide()
    else onShow()
  }

  window.addEventListener('pagehide', onHide)
  window.addEventListener('pageshow', onShow)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }

  return () => {
    window.removeEventListener('pagehide', onHide)
    window.removeEventListener('pageshow', onShow)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }
}

export { Client } from './client'
export { CloseYourItLogger } from './logger'
export { Scope } from './scope'
export { SDK_NAME, SDK_VERSION } from './version'
export type { StatsSnapshot } from './stats'
export type {
  AutoBreadcrumbsOptions,
  BeforeSend,
  CaptureHint,
  CloseYourItOptions,
  DiagnosticDetails,
  DiagnosticEvent,
  DropReason,
  EventLevel,
  EventPayload,
  LogLevel,
  OnDiagnostic,
  TraceCarrier,
  TracePropagationTarget,
  UserContext,
} from './types'
